<template>
  <v-container class="my-5">
    <v-layout row wrap class="justify-center">
      <v-flex xs12>
        <v-card-title>
          <v-btn icon @click="$router.go(-1)" class="mr-3">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          {{ $t('sports') }}
          <v-spacer></v-spacer>
          <v-btn v-if="(getProfile.role === 1) && (getProfile.department_manager === true || getProfile.staff)" color="primary" @click="openDialog()">
            + {{ $t('add_sport') }}
          </v-btn>
        </v-card-title>

        <v-row v-if="sports.length > 0" class="mt-2">
          <v-col v-for="item in sports" :key="item.id" cols="12" sm="6" md="4" lg="3">
            <v-card
              class="elevation-1 d-flex flex-column sport-card"
              style="border-radius: 12px; height: 100%; border: 1px solid #eeeeee; transition: box-shadow 0.2s ease, border-color 0.2s ease; cursor: pointer;"
              @click="viewDetails(item)"
            >
              <!-- Emoji / Icon area -->
              <div class="d-flex flex-column align-center pt-5 pb-3 px-3">
                <div
                  class="sport-emoji-circle mb-3"
                  :style="`background: ${item.color ? item.color + '18' : '#f5f5f5'};`"
                >
                  <span style="font-size: 34px; line-height: 1;">{{ getSportEmoji(item.name) }}</span>
                </div>
                <div class="text-center">
                  <span class="subtitle-1 font-weight-bold grey--text text--darken-3" style="line-height: 1.3;">{{ getSportName(item.name) }}</span>
                </div>
              </div>

              <!-- Description -->
              <v-card-text class="flex-grow-1 pt-0 pb-2 text-center">
                <p
                  v-if="item.description"
                  class="caption mb-0 grey--text text--darken-1"
                  style="display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; line-height: 1.5;"
                >
                  {{ item.description }}
                </p>
                <p v-else class="caption mb-0 font-italic grey--text">
                  {{ $t('no_description') || 'Sin descripción' }}
                </p>
              </v-card-text>

              <v-divider></v-divider>

              <!-- Actions -->
              <v-card-actions class="px-3 py-1" @click.stop>
                <v-spacer></v-spacer>
                <template v-if="getProfile.role === 1">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small class="mr-1" v-bind="attrs" v-on="on" @click.stop="editItem(item)">
                        <v-icon x-small color="blue-grey darken-1">mdi-pencil-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('modificar') || 'Editar' }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon x-small v-bind="attrs" v-on="on" @click.stop="confirmDelete(item)">
                        <v-icon x-small color="red lighten-2">mdi-trash-can-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t('delete') || 'Eliminar' }}</span>
                  </v-tooltip>
                </template>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>

        <v-layout v-else-if="!loadingData" align-center justify-center class="py-10">
          <v-flex class="text-center grey--text">
            <v-icon large color="grey lighten-1">mdi-alert-circle-outline</v-icon>
            <h3 class="mt-2">{{ $t('no_records_yet') || 'No hay deportes registrados aún.' }}</h3>
          </v-flex>
        </v-layout>

        <v-layout v-if="loadingData" align-center justify-center class="py-10">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </v-layout>
      </v-flex>
    </v-layout>

    <!-- Dialog create/edit -->
    <v-dialog v-model="dialog" max-width="850px" persistent>
      <v-form ref="form" v-model="valid" @submit.prevent="save">
        <v-card style="border-radius: 14px; overflow: hidden;">

          <!-- Header -->
          <div class="d-flex align-center px-5 pt-5 pb-4" style="gap: 14px;">
            <div
              class="sport-emoji-circle"
              :style="`background: ${editedItem.color ? editedItem.color + '18' : '#f5f5f5'}; flex-shrink: 0;`"
            >
              <span style="font-size: 28px; line-height: 1;">{{ getSportEmoji(editedItem.name) }}</span>
            </div>
            <div class="flex-grow-1">
              <div class="overline grey--text mb-0" style="line-height: 1;">{{ update ? ($t('modificar') || 'Editar') : ($t('add_sport') || 'Agregar') }}</div>
              <div class="title font-weight-bold grey--text text--darken-3" style="line-height: 1.3;">
                {{ getSportName(editedItem.name) || ($t('add_sport') || 'Nuevo deporte') }}
              </div>
            </div>
            <v-btn icon small @click="close"><v-icon>mdi-close</v-icon></v-btn>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pt-6 pb-4">
            <v-row>
              <!-- Left Column: Inputs -->
              <v-col cols="12" md="5" class="pl-md-6 pr-md-4">
                <v-text-field
                  v-model="editedItem.name"
                  :label="'*' + $t('nombre')"
                  prepend-icon="mdi-format-title"
                  outlined
                  :rules="[rules.required]"
                  :error-messages="errores.name"
                  class="mb-3"
                ></v-text-field>
                <v-textarea
                  v-model="editedItem.description"
                  :label="$t('description') || 'Descripción'"
                  prepend-icon="mdi-text"
                  outlined
                  counter
                  rows="6"
                  maxlength="500"
                  :error-messages="errores.description"
                ></v-textarea>
              </v-col>

              <!-- Right Column: Color Picker -->
         <v-col cols="12" md="7" class="d-flex flex-column pr-md-6 pl-md-4">
  <div class="caption grey--text mb-3 ml-2">{{ $t('select_color') || 'Color de acento' }}</div>
  <div class="color-picker-wrapper">
    <v-color-picker
      v-model="editedItem.color"
      hide-mode-switch
      dot-size="30"
      canvas-height="210"
      class="elevation-0 full-width-color-picker"
    ></v-color-picker>
  </div>
</v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>
          <v-card-actions class="px-5 py-3">
            <v-btn text small color="grey darken-1" @click="close">{{ $t('cerrar') || 'Cerrar' }}</v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="primary"
              depressed
              small
              :loading="loading"
              type="submit"
              :disabled="!valid"
            >
              {{ update ? ($t('modificar') || 'Guardar cambios') : ($t('agregar') || 'Agregar') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>

    <!-- Dialog Details -->
    <v-dialog v-model="dialogDetails" max-width="420px">
      <v-card style="border-radius: 14px; overflow: hidden;">

        <!-- Header con emoji grande -->
        <div
          class="d-flex flex-column align-center pt-6 pb-5"
          :style="`background: ${viewItem.color ? viewItem.color + '10' : '#fafafa'};`"
        >
          <div
            class="sport-emoji-circle mb-3"
            :style="`background: ${viewItem.color ? viewItem.color + '22' : '#f0f0f0'};`"
            style="width: 80px; height: 80px;"
          >
            <span style="font-size: 40px; line-height: 1;">{{ getSportEmoji(viewItem.name) }}</span>
          </div>
          <div class="title font-weight-bold grey--text text--darken-3 text-center px-4">{{ getSportName(viewItem.name) }}</div>
          <div v-if="viewItem.color" class="mt-2">
            <v-chip
              x-small
              label
              :color="viewItem.color"
              dark
              style="font-size: 11px; letter-spacing: 0.5px;"
            >
              {{ viewItem.color }}
            </v-chip>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Descripción -->
        <v-card-text class="pt-4 pb-3">
          <div class="overline grey--text mb-1">{{ $t('description') || 'Descripción' }}</div>
          <p class="body-2 grey--text text--darken-2 mb-0" style="line-height: 1.6;">
            {{ viewItem.description || ($t('no_description') || 'Sin descripción') }}
          </p>
        </v-card-text>

        <v-divider></v-divider>

        <!-- Acciones -->
        <v-card-actions class="px-4 py-3">
          <v-spacer></v-spacer>
          <v-btn
            v-if="getProfile.role === 1"
            text
            small
            color="blue-grey darken-1"
            @click="dialogDetails = false, editItem(viewItem)"
          >
            <v-icon left small>mdi-pencil-outline</v-icon>
            {{ $t('modificar') || 'Editar' }}
          </v-btn>
          <v-btn
            depressed
            small
            color="grey lighten-3"
            @click="dialogDetails = false"
          >
            {{ $t('cerrar') || 'Cerrar' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Delete -->
    <v-dialog v-model="dialogDelete" max-width="400px">
      <v-card>
        <v-card-title class="headline">{{ $t('delete_sport_title') || $t('msg_delete_notice') || 'Eliminar Deporte' }}</v-card-title>
        <v-card-text>{{ $t('delete_sport_confirm') || $t('msg_accion_deshacer') || '¿Estás seguro de que deseas eliminar este deporte? Esta acción no se puede deshacer.' }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="closeDelete">{{ $t('cancelar') || 'No' }}</v-btn>
          <v-btn color="red darken-1" :loading="loading" text @click="deleteItemConfirm">{{ $t('delete_action') || $t('Si') || 'Sí' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import { mapGetters, mapState } from 'vuex'

const API_BASE_URL = 'https://clubtest.intrare.app'

export default {
  name: 'sports',
  data: () => ({
    dialog: false,
    dialogDetails: false,
    dialogDelete: false,
    loadingData: false,
    loading: false,
    valid: true,
    sports: [],
    errores: [],
    update: false,
    editedItem: {
      id: null,
      name: '',
      description: '',
      color: '#FF6666'
    },
    defaultItem: {
      id: null,
      name: '',
      description: '',
      color: '#FF6666'
    },
    viewItem: {},
    itemToDelete: null
  }),

  computed: {
    ...mapGetters(['getProfile']),
    ...mapState(['language']),
    formTitle () {
      return this.update ? this.editedItem.name : (this.$t('add_sport') || 'Agregar Deporte')
    },
    headers () {
      return [
        { text: this.$t('nombre') || 'Nombre', value: 'name' },
        { text: this.$t('color') || 'Color', value: 'color' },
        { text: this.$t('description') || 'Descripción', value: 'description' },
        { text: this.$t('acciones') || 'Acciones', value: 'actions', sortable: false, align: 'right' }
      ]
    },
    rules () {
      return {
        required: value => !!value || (this.$t('required_field') || 'Campo requerido')
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.resizeColorPickerCanvas()
    })
  },
  created () {
    this.getSports()
  },

  methods: {
    resizeColorPickerCanvas () {
      const wrapper = this.$refs.colorPickerWrapper
      if (!wrapper) return

      const canvas = wrapper.querySelector('.v-color-picker__canvas canvas')
      const container = wrapper.querySelector('.v-color-picker__canvas')

      if (!canvas || !container) return

      const fullWidth = wrapper.offsetWidth

      // Redimensionar el atributo real del canvas
      canvas.setAttribute('width', fullWidth)
      container.style.width = fullWidth + 'px'

      // Forzar re-render del canvas en Vuetify
      // disparando un resize event en el picker
      window.dispatchEvent(new Event('resize'))
    },
    getSportEmoji (name) {
      if (!name) return '🏅'
      // Extrae los caracteres iniciales que sean emoji (unicode > U+00FF y sus secuencias)
      const emojiRegex = /^(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\uFE0F)?(\u200D(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\uFE0F)?)*\s*/u
      const match = name.match(emojiRegex)
      return match ? match[0].trim() : '🏅'
    },
    getSportName (name) {
      if (!name) return ''
      const emojiRegex = /^(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\uFE0F)?(\u200D(\p{Emoji_Presentation}|\p{Extended_Pictographic})(\uFE0F)?)*\s*/u
      return name.replace(emojiRegex, '').trim()
    },
    apiCall (method, endpoint, data = null) {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': this.language,
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      if (data) config.data = data
      return axios(config)
    },

    async getSports () {
      this.loadingData = true
      try {
        const response = await this.apiCall('get', '/sport/')
        this.sports = response.data
      } catch (error) {
        console.error('Error fetching sports:', error)
      } finally {
        this.loadingData = false
      }
    },

    openDialog () {
      this.update = false
      this.errores = []
      this.editedItem = Object.assign({}, this.defaultItem)
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialog = true
    },

    editItem (item) {
      this.update = true
      this.errores = []
      this.editedItem = Object.assign({}, item)
      if (!this.editedItem.color) this.editedItem.color = '#FF6666'
      if (this.$refs.form) this.$refs.form.resetValidation()
      this.dialog = true
    },

    viewDetails (item) {
      this.viewItem = Object.assign({}, item)
      this.dialogDetails = true
    },

    close () {
      this.dialog = false
    },

    async save () {
      if (!this.$refs.form.validate()) return
      this.loading = true
      this.errores = []

      try {
        if (this.update) {
          const payload = {
            name: this.editedItem.name,
            description: this.editedItem.description || null,
            color: this.editedItem.color
          }
          await this.apiCall('patch', `/sport/${this.editedItem.id}`, payload)
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('msg_sport_updated') || 'Actualizado correctamente')
        } else {
          const payload = {
            idHousingUnit: this.getProfile.idHousingUnit,
            name: this.editedItem.name,
            description: this.editedItem.description || null,
            color: this.editedItem.color
          }
          await this.apiCall('post', '/sport/', payload)
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_message', this.$t('msg_sport_added') || 'Agregado correctamente')
        }
        await this.getSports()
        this.close()
      } catch (error) {
        if (error.response && error.response.status === 400 && error.response.data) {
          this.errores = error.response.data
        } else {
          console.error(error)
        }
      } finally {
        this.loading = false
      }
    },

    confirmDelete (item) {
      this.itemToDelete = item
      this.dialogDelete = true
    },

    closeDelete () {
      this.dialogDelete = false
      this.itemToDelete = null
    },

    async deleteItemConfirm () {
      if (!this.itemToDelete) return
      this.loading = true
      try {
        await this.apiCall('delete', `/sport/${this.itemToDelete.id}`)
        this.$store.commit('snackbar_activate_true')
        this.$store.commit('snackbar_change_color', 'warning')
        this.$store.commit('snackbar_change_message', this.$t('msg_notice_deleted') || 'Eliminado correctamente')
        await this.getSports()
      } catch (error) {
        console.error('Error deleting sport:', error)
      } finally {
        this.loading = false
        this.closeDelete()
      }
    }
  }
}
</script>

<style scoped>
.sport-emoji-circle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
}

.sport-card:hover {
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.10) !important;
}

::v-deep .full-width-color-picker {
  width: 100% !important;
  max-width: 100% !important;
}

::v-deep .full-width-color-picker .v-color-picker__canvas,
::v-deep .full-width-color-picker .v-color-picker__controls,
::v-deep .full-width-color-picker .v-color-picker__edit {
  width: 100% !important;
  max-width: 100% !important;
}

/* Wrapper que controla el ancho total */
.color-picker-wrapper {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

/* Forzar que el picker y todos sus hijos ocupen el 100% */
.color-picker-wrapper ::v-deep .v-color-picker {
  width: 100% !important;
  max-width: 100% !important;
  box-shadow: none !important;
}

/* Remover padding del body interno */
.color-picker-wrapper ::v-deep .v-picker__body {
  width: 100% !important;
  max-width: 100% !important;
  padding: 0 !important;
}

/* El contenedor del canvas */
.color-picker-wrapper ::v-deep .v-color-picker__canvas {
  width: 100% !important;
  max-width: 100% !important;
}

/* El canvas HTML real (tiene width en px por atributo) */
.color-picker-wrapper ::v-deep .v-color-picker__canvas canvas {
  width: 100% !important;
  height: 210px !important;
}

/* Controles (hue slider, alpha) */
.color-picker-wrapper ::v-deep .v-color-picker__controls {
  width: 100% !important;
  max-width: 100% !important;
  padding: 8px 16px !important;
}

/* Sliders internos */
.color-picker-wrapper ::v-deep .v-color-picker__preview {
  width: 100% !important;
}

.color-picker-wrapper ::v-deep .v-color-picker__edit {
  width: 100% !important;
  max-width: 100% !important;
}

.color-picker-wrapper ::v-deep .v-color-picker__canvas canvas {
  width: 100% !important;
  height: 210px !important;
}

</style>
